import type { CollectionSummaryJSONObject, TagJSONObject } from '@ikegps/core-api';
import { IkeToken } from '@ikegps/core-api';
import { MaterialIcon } from '../components/icon/icons';
import { SortableState, type SortableStateOption } from '../components/sortable/sortable.state';
import { Translate } from '../i18n';
import { T } from '../i18n';
import { Services } from '../services';
import { AppStore } from '../store';
import { userDisplayName } from '../util/userDisplayName';

function getCollectedBy(obj: CollectionSummaryJSONObject): string | null {
	if (obj.collectedBy == null) {
		return null;
	}

	const user = AppStore.UserProfile.get(obj.collectedBy);
	if (user == null) {
		return null;
	}

	return user.username;
}

function getUpdatedAt(obj: CollectionSummaryJSONObject | TagJSONObject): string {
	return obj.updatedAt || obj.createdAt;
}

function getDisplayUpdatedAt(obj: CollectionSummaryJSONObject | TagJSONObject): Date {
	return obj.updatedAt != null ? Services.DateTime.getDate(obj.updatedAt) : Services.DateTime.getDate(obj.createdAt);
}

function getAssignedTo(obj: CollectionSummaryJSONObject): string {
	const userProfile = AppStore.UserProfile.get(obj.assignedTo);
	if (userProfile != null) {
		return userDisplayName(userProfile);
	}

	return Translate(T.COLLECTION_UNASSIGNED);
}

const UNSORTED_KEY = '';

const SORT_OPTIONS: SortableStateOption[] = [
	{
		title: T.SORT_FIELD_UPDATED_AT,
		icon: MaterialIcon.UPDATE,
		keys: {
			tag: getUpdatedAt,
			collection: getUpdatedAt,
		},
		value: {
			collection: getDisplayUpdatedAt,
		},
		info: {
			tag: true,
			collection: true,
		},
	},
	{
		title: T.SORT_FIELD_COLLECTED_AT,
		icon: MaterialIcon.DATE,
		keys: {
			tag: 'createdAt',
			collection: (summary: CollectionSummaryJSONObject): string => {
				return summary.collectedAt != null ? summary.collectedAt.iso : summary.createdAt;
			},
		},
		value: {
			collection: (summary: CollectionSummaryJSONObject): Date => {
				return summary.collectedAt != null
					? Services.DateTime.getDate(summary.collectedAt.iso)
					: Services.DateTime.getDate(summary.createdAt);
			},
		},
		info: {
			tag: true,
			collection: true,
		},
	},
	{
		title: T.SORT_FIELD_NAME,
		icon: MaterialIcon.SHORT_TEXT,
		keys: {
			tag: 'name',
			collection: 'title',
		},
		info: {},
	},
	{
		title: T.SORT_FIELD_COLLECTED_BY,
		icon: MaterialIcon.PERSON,
		keys: {
			tag: UNSORTED_KEY,
			collection: getCollectedBy,
		},
		info: {
			collection: true,
		},
	},
	{
		title: T.SORT_FIELD_STATUS,
		icon: MaterialIcon.STATUS,
		visible: () =>
			Services.Token.has(IkeToken.FEATURE_SORT_STATUS) || Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE),
		keys: {
			tag: UNSORTED_KEY,
			collection: 'status',
		},
		info: {
			collection: true,
		},
	},
	{
		title: T.SORT_FIELD_ASSIGNED,
		icon: MaterialIcon.ASSIGN,
		visible: () =>
			Services.Token.has(IkeToken.FEATURE_COLLECTION_ASSIGN) || Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE),
		keys: {
			tag: UNSORTED_KEY,
			collection: getAssignedTo,
		},
		info: {
			collection: true,
		},
	},
];

export const TagSortState = new SortableState(SORT_OPTIONS);
export const MAX_TAG_SHOWN = 50;
